<template>
  <div class="content container">
    <h1 class="heading">Upload a CSV with new installations</h1>
    <a :href="downloadUrl" class="btn btn--xl btn--main btn--action"
      ><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <path
          fill="#929292"
          fill-rule="evenodd"
          d="M15 27.3L11.7 24l-.7.8 4.5 4.4 4.5-4.4-.8-.8-3.2 3.3V16h-1v11.3zM14 22H7a4 4 0 0 1-.9-7.9 6 6 0 0 1 11.6-3c.7-.7 1.7-1.1 2.8-1.1 2.4 0 4.3 1.8 4.5 4.1a4 4 0 0 1-1 7.9h-7v1h7a5 5 0 0 0 1.9-9.6A5.5 5.5 0 0 0 18 9.6a7 7 0 0 0-13 3.8A5 5 0 0 0 7 23h7v-1z"
        />
      </svg>
      Download Latest Installations CSV</a
    >

    <div class="max-w-md mx-auto">
      <file-pond
        name="test"
        ref="pond"
        instantUpload="false"
        label-idle="Drag & Drop your CSV or <span class='filepond--label-action'> Browse </span>"
        allow-multiple="false"
        max-files="1"
        :server="uploadUrl"
        :allowRevert="false"
        v-bind:files="csvFile"
        @addfile="fileSelected"
        @removefile="fileRemoved"
      />
    </div>

    <transition class="fade">
      <div class="max-w-md mx-auto" v-if="checkedCSV">
        <div
          v-if="!stats.total"
          class="text-red-darkest border-l-4 border-red-dark bg-red-lightest px-8 py-4"
        >
          No valid installations were found in the chosen CSV.
        </div>

        <div v-if="stats.total" class="csv-stats">
          <h3 class="small-heading">This is what we found in the CSV.</h3>
          <p>
            <strong>{{ stats.total }}</strong> valid rows were found in the CSV.
          </p>

          <div>
            <p>
              <strong>{{ stats.installationsWhichWillBeUpdated.length }}</strong> already existing
              installations will be updated.
            </p>

            <div class="csv-stats__samples">
              <ul>
                <li
                  v-for="installation in stats.installationsWhichWillBeUpdated"
                  :key="installation.name"
                >
                  {{ installation.name }}
                </li>
              </ul>
            </div>
          </div>

          <div v-if="stats.groupedNames.length">
            <p class="pb-1">
              <strong>{{ stats.groupedNames.length }}</strong> new groups will be created.
            </p>
            <div class="csv-stats__samples">
              <ul>
                <li v-for="installation in stats.groupedNames" :key="installation.name">
                  {{ installation.count }} will be <strong>{{ installation.name }}</strong>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="stats.groupedExisting.length">
            <p class="pb-1">
              New installations will be added to
              <strong>{{ stats.groupedExisting.length }}</strong> existing group.
            </p>
            <div class="csv-stats__samples">
              <ul>
                <li v-for="installation in stats.groupedExisting" :key="installation.name">
                  {{ installation.count }} will be added to <strong>{{ installation.name }}</strong>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="stats.installationsWithNoId.length">
            <p>
              IDs will be generated for
              <strong>{{ stats.installationsWithNoId.length }}</strong> installations.
            </p>
            <div class="csv-stats__samples">
              <ul>
                <li v-for="installation in stats.installationsWithNoId" :key="installation">
                  {{ installation.name }}
                </li>
              </ul>
            </div>
          </div>

          <div v-if="stats.installationsMissingData.coords.withoutPostCode.length">
            <p>
              Latitude and Longitude coordinates are <strong>missing</strong> and
              <strong>cannot</strong> be created for
              <strong>{{ stats.installationsMissingData.coords.withoutPostCode.length }}</strong>
              installations.
            </p>
            <div class="csv-stats__samples">
              <ul>
                <li
                  v-for="installation in stats.installationsMissingData.coords.withoutPostCode"
                  :key="installation.name"
                >
                  {{ installation.name }}
                </li>
              </ul>
            </div>
          </div>

          <div v-if="stats.installationsMissingData.localAuthority.withoutPostCode.length">
            <p>
              The Local Authority is <strong>missing</strong> and <strong>cannot</strong> be created
              for
              <strong>{{
                stats.installationsMissingData.localAuthority.withoutPostCode.length
              }}</strong>
              installations.
            </p>
            <div class="csv-stats__samples">
              <ul>
                <li
                  v-for="installation in stats.installationsMissingData.localAuthority
                    .withoutPostCode"
                  :key="installation.name"
                >
                  {{ installation.name }}
                </li>
              </ul>
            </div>
          </div>

          <div class="flex flex-row">
            <div class="flex-1 flex flex-col">
              <div class="font-normal normal-case text-xs text-center">Yes, that looks good.</div>
              <button class="btn btn--xl btn--main" @click="uploadForm">Upload CSV</button>
            </div>
            <div class="flex-1 flex flex-col">
              <div class="font-normal normal-case text-xs text-center">
                No, something looks wrong.
              </div>
              <button class="btn btn--xl btn--warn" @click="removeCSV">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div>
      <hr />

      <h2 class="subheading">Some things to take into account before uploading the CSV.</h2>

      <ol>
        <li>Values in the CSV do <strong>not</strong> need units.</li>
        <li>
          Make sure that fields which can be the same for many installations are all spelled and
          cased the same way.
          <ul class="mb-4">
            <li>names</li>
            <li>owners</li>
            <li>ownershipType</li>
            <li>energyType</li>
            <li>source</li>
          </ul>
        </li>
        <li>The CSV file can contain these columns:</li>
      </ol>

      <table class="styled-table">
        <thead class="sticky bg-white" style="top: 64px">
          <th>Column Name</th>
          <th>Required</th>
          <th>Description</th>
          <th style="width: 180px">Example</th>
        </thead>
        <tr>
          <td>name</td>
          <td>required</td>
          <td>
            The name of the installation. Installations with the same name will be grouped together.
          </td>
          <td>Chilton County Primary</td>
        </tr>
        <tbody>
          <tr>
            <td>annualPredictedGeneration</td>
            <td>optional</td>
            <td>
              How much energy is the installation going to generate annually in kWh.<br />Only
              applicable to <strong>Renewable energy</strong> installations.
            </td>
            <td>10000</td>
          </tr>
          <tr>
            <td>commissioned</td>
            <td>optional</td>
            <td>
              Date in which the installation was commissioned.<br />In the future it would be nice
              to be able to standardise these.
            </td>
            <td>31/12/2018</td>
          </tr>
          <tr>
            <td>energyType</td>
            <td>optional</td>
            <td>
              The energy type the installation harnessses.<br />Only applicable to
              <strong>Renewable energy</strong> installations.
            </td>
            <td>
              Air Source Heat<br />
              Combined Heat &amp; Power<br />
              Heat Pumps<br />
              Hydro<br />
              Solar PV<br />
              Solar Thermal<br />
              Wind
            </td>
          </tr>

          <tr>
            <td>capacity</td>
            <td>optional</td>
            <td>
              How much capacity the installation has in kW.<br />Only applicable to
              <strong>Renewable energy</strong> installations.
            </td>
            <td>10000</td>
          </tr>
          <tr>
            <td>info</td>
            <td>optional</td>
            <td>
              A paragraph or two with some extra information you wish to share about the
              installation.
            </td>
            <td></td>
          </tr>
          <tr>
            <td>postcode</td>
            <td>optional</td>
            <td>
              <p>
                Postcodes are not stored, but they are used to generate other properties if they
                have not been provided.
              </p>
              <p>These properties include:<br />lat, lng, localAuthority</p>
            </td>
            <td>OX7 3QA</td>
          </tr>
          <tr>
            <td>localAuthority</td>
            <td>optional</td>
            <td>
              The local authority the installation is in.<br />It is used for filtering purposes
              only.
            </td>
            <td>Buckinghamshire</td>
          </tr>
          <tr>
            <td>location</td>
            <td>optional</td>
            <td>
              The name of a location where the installation can be found.<br />It is displayed in
              the installation's page only.
            </td>
            <td>Kirklington</td>
          </tr>
          <tr>
            <td>owner</td>
            <td>optional</td>
            <td>The owner of the installation.</td>
            <td>Home Owner</td>
          </tr>
          <tr>
            <td>ownershipType</td>
            <td>optional</td>
            <td>The type of ownership of the installation.</td>
            <td>Community</td>
          </tr>
          <tr>
            <td>public</td>
            <td>optional</td>
            <td>
              Should this installation appear on external sites using the API. eg.
              <a href="https://oxford.uk100.org/" target="_blank">uk100</a>
            </td>
            <td>1<br />0</td>
          </tr>
          <tr>
            <td>supportedBy</td>
            <td>optional</td>
            <td></td>
            <td>OxFutures<br />TOE2</td>
          </tr>
          <tr>
            <td>source</td>
            <td>optional</td>
            <td>
              If data needs to be imported periodically with scripts, we must specify the source of
              this data. The ID of the installation will be used to make a relationship and download
              any future data.
            </td>
            <td>rTone</td>
          </tr>
          <tr>
            <td>url</td>
            <td>optional</td>
            <td>
              A link to a website with more information.<br />Should be the full link, including
              <strong>https://</strong>.
            </td>
            <td>https://google.com</td>
          </tr>
          <tr>
            <td>type</td>
            <td>optional</td>
            <td>The type of installation it is.</td>
            <td>
              Energy Efficiency<br />
              Renewable Energy
            </td>
          </tr>
          <tr>
            <td>quickbase.generation</td>
            <td>optional</td>
            <td>
              If the generation data should be stored in QuickBase, provide its QB ID to create a
              relationship.
            </td>
            <td>1234</td>
          </tr>
          <tr>
            <td>quickbase.export</td>
            <td>optional</td>
            <td>
              If the export data should be stored in QuickBase, provide its QB ID to create a
              relationship.
            </td>
            <td>4321</td>
          </tr>
          <tr>
            <td>interventions.*</td>
            <td>optional</td>
            <td>
              <p>
                Each possible intervention should have its own column.<br />The name of the
                intervention should be prefixed with <strong>interventions.</strong>
              </p>

              <p>
                eg.<br />
                <strong>interventions.Insulation</strong><br />
                <strong>interventions.LED lighting</strong><br />
                <strong>interventions.total</strong><br />
                <strong>interventions.co2</strong>
              </p>

              <p>
                The value for the field should be a <strong>1</strong> if the installation has it.
                Or <strong>0</strong> if it does not.
              </p>
            </td>
            <td>1</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import vueFilePond from 'vue-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';

const FilePond = vueFilePond(FilePondPluginFileValidateType);

export default {
  data() {
    return {
      stats: {
        total: 0,
        installationsWhichWillBeUpdated: [],
        groupedNames: [],
        groupedExisting: []
      },
      checkedCSV: false,
      csvFile: []
    };
  },
  computed: {
    uploadUrl() {
      const url = process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : '';
      const path = '/api/v2/installations/csv';
      const token = this.$store.getters.token;
      return `${url}${path}?access_token=${token}`;
    },
    downloadUrl() {
      const url = process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : '';
      const path = '/api/v2/installations/csv-download';
      const token = this.$store.getters.token;
      return `${url}${path}?access_token=${token}`;
    }
  },
  components: {
    FilePond
  },
  methods: {
    fileSelected: function() {
      const input = this.$refs.pond.getFiles()[0];
      const formData = new FormData();
      formData.append('file', input.file);

      // You should have a server side REST API
      axios
        .post('/api/v2/installations/csv-check', formData)
        .then(res => {
          this.checkedCSV = true;
          this.stats = res.data;
        })
        .catch(err => {
          this.$snotify.error('There is a problem with the file selected.', {
            pauseOnHover: true,
            showProgressBar: true,
            timeout: 3000
          });
        });
    },
    fileRemoved: function() {
      this.checkedCSV = false;
    },
    uploadForm: function() {
      this.checkedCSV = false;
      this.$refs.pond.processFiles();
    },
    removeCSV: function() {
      this.$refs.pond.removeFiles();
    }
  }
};
</script>

<style lang="sass">
.filepond--root
  margin-bottom: 0

.filepond--action-process-item
  display: none

.csv-stats
  background: #f1f0ef
  @apply rounded-b-lg
  @apply text-blue-darkest
  @apply text-blue-darkest
  @apply -mt-2
  @apply px-8
  @apply py-4

.csv-stats__samples
  @apply -mt-4
  @apply mb-8
  @apply overflow-auto
  max-height: 60px
</style>
